import React from 'react';
import { ShoppingCart } from 'lucide-react';
import WishlistButton from '@/components/WishlistButton';
import { telLink } from '@/utils/phone';
import { getDisplayPrice } from '@/utils/pricing';

/**
 * Sticky right-rail summary card: product image, running price breakdown, CTA.
 *
 * Props:
 *   product: { product_id, name, manufacturer, image_url, price }
 *   lineItems: Array<{ label: string, amount: number, sub?: string }>  // +amounts for extras
 *   total: number
 *   onAdd: () => void
 *   adding: boolean
 *   stock: number
 */
export default function BuildSummaryCard({
  product,
  lineItems = [],
  total,
  onAdd,
  adding,
  stock,
  baseLabel = 'Base',
  basePrice,
}) {
  if (!product) return null;
  const isCallForPricing = product.is_call_for_pricing;
  const baseAmount = basePrice !== undefined ? basePrice : getDisplayPrice(product);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
      data-testid="build-summary-card"
    >
      <div className="flex items-start gap-3 mb-4 pb-4 border-b border-gray-100">
        <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 flex items-center justify-center">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {product.manufacturer}
          </p>
          <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
            {product.name}
          </p>
        </div>
        <WishlistButton productId={product.product_id} className="p-2 flex-shrink-0" />
      </div>

      {isCallForPricing ? (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-center" data-testid="build-summary-call-for-pricing">
          <p className="text-sm font-bold text-amber-800">Call for Pricing</p>
          <p className="text-xs text-amber-700 mt-1">Not available for online purchase right now</p>
          {product.call_for_pricing_phone && (
            <a
              href={telLink(product.call_for_pricing_phone)}
              className="text-sm font-bold text-amber-900 underline mt-1 inline-block"
              data-testid="build-summary-call-for-pricing-phone"
            >
              {product.call_for_pricing_phone}
            </a>
          )}
        </div>
      ) : (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-2">
            Your Build
          </p>
          <ul className="space-y-1.5 mb-4" data-testid="build-summary-lines">
            <li className="flex items-baseline justify-between text-sm">
              <span className="text-gray-600">{baseLabel}</span>
              <span className="text-gray-900 tabular-nums">${baseAmount?.toFixed(2)}</span>
            </li>
            {lineItems.map((li, idx) => (
              <li key={idx} className="flex items-baseline justify-between text-sm">
                <span className="text-gray-600 truncate pr-2">
                  {li.label}
                  {li.sub && <span className="text-gray-400 text-xs"> · {li.sub}</span>}
                </span>
                <span
                  className={`tabular-nums ${li.amount > 0 ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  {li.amount > 0 ? `+$${li.amount.toFixed(2)}` : 'Included'}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex items-baseline justify-between pt-3 border-t border-gray-200 mb-4">
            <span className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Total</span>
            <span className="text-2xl font-bold text-[#334155] tabular-nums" data-testid="build-total">
              ${total.toFixed(2)}
            </span>
          </div>
        </>
      )}

      <button
        onClick={onAdd}
        disabled={adding || stock === 0 || isCallForPricing}
        className="w-full bg-[#334155] text-white font-bold uppercase tracking-wider text-sm py-3 rounded-lg hover:bg-[#475569] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        data-testid="build-summary-add"
      >
        <ShoppingCart className="w-4 h-4" />
        {adding ? 'Adding...' : isCallForPricing ? 'Call for Pricing' : stock === 0 ? 'Out of Stock' : 'Add to Cart'}
      </button>

      {stock > 0 && !isCallForPricing && (
        <p className="text-[11px] text-center text-gray-500 mt-3" data-testid="stock-status">
          {stock <= 5 ? (
            <span className="text-[#EF4444] font-semibold">Only {stock} left</span>
          ) : (
            <>In stock</>
          )}
        </p>
      )}
    </div>
  );
}
